import api from '@/lib/api';

export type OrganizationRole = 'OWNER' | 'EDITOR' | 'VIEWER';
export type InvitableRole = Exclude<OrganizationRole, 'OWNER'>;

export interface Organization {
  id: string;
  name: string;
  role: OrganizationRole;
  default: boolean;
  created: string;
}

export interface OrganizationMember {
  userId: string;
  username: string;
  givenName: string | null;
  familyName: string | null;
  role: OrganizationRole;
  joined: string;
}

export interface OrganizationInvitation {
  id: string;
  email: string;
  role: InvitableRole;
  expiresAt: string;
  createdAt: string;
}

export interface CurrentUser {
  id: string;
  username: string;
  givenName: string | null;
  familyName: string | null;
  defaultOrganizationId: string | null;
}

const PATH = 'organizations';

function orgPath(organizationId: string) {
  return `${PATH}/${encodeURIComponent(organizationId)}`;
}

export async function getCurrentUser() {
  const { data } = await api.get<CurrentUser>('user/me');
  return data;
}

export async function listOrganizations() {
  const { data } = await api.get<Organization[]>(PATH);
  return data;
}

export async function createOrganization(name: string) {
  const { data } = await api.post<Organization>(PATH, { name });
  return data;
}

export async function renameOrganization(organizationId: string, name: string) {
  const { data } = await api.patch<Organization>(orgPath(organizationId), { name });
  return data;
}

export async function listMembers(organizationId: string) {
  const { data } = await api.get<OrganizationMember[]>(`${orgPath(organizationId)}/members`);
  return data;
}

export async function changeMemberRole(
  organizationId: string,
  userId: string,
  role: InvitableRole
) {
  await api.patch(`${orgPath(organizationId)}/members/${encodeURIComponent(userId)}`, { role });
}

/** Removes a member, or leaves the organization when `userId` is the caller's own id. */
export async function removeMember(organizationId: string, userId: string) {
  await api.delete(`${orgPath(organizationId)}/members/${encodeURIComponent(userId)}`);
}

export async function listInvitations(organizationId: string) {
  const { data } = await api.get<OrganizationInvitation[]>(
    `${orgPath(organizationId)}/invitations`
  );
  return data;
}

export async function createInvitation(organizationId: string, email: string, role: InvitableRole) {
  const { data } = await api.post<OrganizationInvitation>(
    `${orgPath(organizationId)}/invitations`,
    { email, role }
  );
  return data;
}

export async function revokeInvitation(organizationId: string, invitationId: string) {
  await api.delete(`${orgPath(organizationId)}/invitations/${encodeURIComponent(invitationId)}`);
}

export async function resendInvitation(organizationId: string, invitationId: string) {
  const { data } = await api.post<OrganizationInvitation>(
    `${orgPath(organizationId)}/invitations/${encodeURIComponent(invitationId)}/resend`
  );
  return data;
}

export async function acceptInvitation(token: string) {
  const { data } = await api.post<Organization>(`${PATH}/invitations/accept`, { token });
  return data;
}
